import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Instagram, Facebook, Mail, MessageCircle } from "lucide-react";
import { useLang } from "@/i18n/LangContext";
import { useContent } from "@/cms/ContentContext";

const Footer = () => {
  const { t } = useLang();
  const { content } = useContent();
  const ch = content?.channels || {};
  const email = ch.email || "ola@ordobyisa.pt";
  const whatsapp = ch.whatsapp || "+351 912 345 678";
  const wa = whatsapp.replace(/\D/g, "");
  const ig = ch.instagram_url || "https://instagram.com/ordobyisa";
  const fb = ch.facebook_url || "https://facebook.com/ordobyisa";
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end end"] });
  const y = useTransform(scrollYProgress, [0, 1], ["30%", "0%"]);

  return (
    <footer ref={ref} data-testid="site-footer" className="relative bg-[#3A3524] text-[#F5F2EC] pt-20 md:pt-28 pb-10 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-8 mb-16 md:mb-24">
          <div className="col-span-12 md:col-span-6 flex flex-col gap-6">
            <div className="overline text-[#F5F2EC]/60">— {t.footer.tag}</div>
            <p className="font-serif italic text-2xl md:text-3xl leading-snug max-w-md text-[#F5F2EC]/85">
              “A ordem começa quando escolhemos apenas o que amamos.”
            </p>
          </div>
          <div className="col-span-6 md:col-span-3 flex flex-col gap-3 text-sm">
            <div className="overline text-[#F5F2EC]/50 mb-2">Navigate</div>
            {["#inicio", "#sobre", "#servicos", "#portfolio", "#testemunhos", "#contacto"].map((h) => (
              <a key={h} href={h} className="text-[#F5F2EC]/80 hover:text-[#F5F2EC] transition-colors">
                {h.replace("#", "")}
              </a>
            ))}
          </div>
          <div className="col-span-6 md:col-span-3 flex flex-col gap-3 text-sm">
            <div className="overline text-[#F5F2EC]/50 mb-2">Contact</div>
            <a href={`mailto:${email}`} className="text-[#F5F2EC]/80 hover:text-[#F5F2EC] transition-colors">{email}</a>
            <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" className="text-[#F5F2EC]/80 hover:text-[#F5F2EC] transition-colors">{whatsapp}</a>
            <div className="flex items-center gap-3 mt-2">
              <a aria-label="Instagram" href={ig} target="_blank" rel="noreferrer" className="hover:text-[#B0926A] transition-colors"><Instagram size={16} strokeWidth={1.4} /></a>
              <a aria-label="Facebook" href={fb} target="_blank" rel="noreferrer" className="hover:text-[#B0926A] transition-colors"><Facebook size={16} strokeWidth={1.4} /></a>
              <a aria-label="WhatsApp" href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" className="hover:text-[#B0926A] transition-colors"><MessageCircle size={16} strokeWidth={1.4} /></a>
              <a aria-label="Email" href={`mailto:${email}`} className="hover:text-[#B0926A] transition-colors"><Mail size={16} strokeWidth={1.4} /></a>
            </div>
          </div>
        </div>

        {/* Massive brand */}
        <motion.div
          style={{ y }}
          className="font-serif tracking-tighter leading-[0.85] text-[#F5F2EC]/95 text-[28vw] md:text-[22vw] lg:text-[20vw] select-none"
          data-testid="brand-lockup"
        >
          ORDO
        </motion.div>

        <div className="mt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-[#F5F2EC]/60 tracking-wider">
          <div>© {new Date().getFullYear()} ORDO by Isa. {t.footer.rights}.</div>
          <div className="flex items-center gap-6">
            <Link data-testid="footer-terms" to="/termos" className="hover:text-[#F5F2EC] transition-colors">
              {t.footer.terms}
            </Link>
            <a href="#inicio" data-testid="footer-back-top" className="hover:text-[#F5F2EC] transition-colors">
              {t.footer.backTop} ↑
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
